import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  const portalAnchorRef = useRef<HTMLSpanElement>(null);
  const [portalTarget, setPortalTarget] = useState<Element | DocumentFragment | null>(null);

  useLayoutEffect(() => {
    const anchor = portalAnchorRef.current;
    if (!anchor) return;

    const widgetRoot = anchor.closest('.widget-root');
    if (widgetRoot) {
      setPortalTarget(widgetRoot);
      return;
    }

    const rootNode = anchor.getRootNode();
    setPortalTarget(rootNode instanceof ShadowRoot ? rootNode : document.body);
  }, []);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const modal = isOpen && portalTarget ? createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header with close button */}
        <div className="modal-header">
          {title && <h2 className="modal-title">{title}</h2>}
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Modal body */}
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>,
    portalTarget
  ) : null;

  return (
    <>
      <span ref={portalAnchorRef} hidden />
      {modal}
    </>
  );
};
